import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, FileSignature, Image as ImageIcon, PhoneCall } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { fmtMoney, todayISO } from "../../lib/format";
import { Modal, StatusBadge, StatusSelect, fmtDate } from "./shared";
import { ReceiptFormModal } from "./ReceiptFormModal";

function TypeBadge({ q }) {
  if (q.request_type !== "booking") return <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-sm bg-slate-100 text-slate-700">Quote</span>;
  return (
    <span className="inline-flex flex-col gap-1">
      <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-sm bg-[#1E2A78] text-white">Booking</span>
      {q.requires_confirmation && <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-sm bg-[#FFD700] text-[#1E2A78] inline-flex items-center gap-1" data-testid={`quote-callback-${q.quote_number}`}><PhoneCall className="h-3 w-3" /> Call to confirm</span>}
    </span>
  );
}

function QuotePhotos({ q }) {
  const [urls, setUrls] = useState([]);
  useEffect(() => {
    let made = [];
    Promise.all(q.images.map((img) => api.get(`/quotes/${q.id}/images/${img.id}`, { responseType: "blob" }).then((r) => URL.createObjectURL(r.data))))
      .then((u) => { made = u; setUrls(u); }).catch((e) => toast.error(formatApiError(e)));
    return () => made.forEach((u) => URL.revokeObjectURL(u));
  }, [q]);
  if (!q.images.length) return <p className="text-sm text-slate-400">No photos uploaded.</p>;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="quote-photos">
      {urls.map((u, i) => (
        <a key={u} href={u} target="_blank" rel="noopener noreferrer" className="aspect-square block border border-slate-200 rounded-sm overflow-hidden">
          <img src={u} alt={q.images[i]?.filename} className="w-full h-full object-cover hover:scale-105 transition-transform" />
        </a>
      ))}
    </div>
  );
}

function Info({ label, value }) {
  return <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div><div className="text-sm">{value || "—"}</div></div>;
}

function QuoteDetail({ q, onClose, onReceipt }) {
  return (
    <Modal wide title={`${q.request_type === "booking" ? "Booking" : "Quote"} ${q.quote_number}`} onClose={onClose}>
      <div className="space-y-5" data-testid="quote-detail">
        <div className="grid sm:grid-cols-4 gap-4">
          <Info label="Customer" value={q.full_name} /><Info label="Phone" value={q.phone} /><Info label="Email" value={q.email} /><Info label="Received" value={fmtDate(q.created_at)} />
          <Info label="Route" value={`${q.origin} → ${q.destination}`} /><Info label="Method" value={q.method === "pickup" ? "Pickup at door" : "Drop-off"} />
          <Info label="Address" value={[q.address, q.city, q.province_state, q.postal_code].filter(Boolean).join(", ")} /><Info label="Preferred date" value={q.preferred_date} />
          <Info label="Receiver" value={q.receiver_name} /><Info label="Receiver phone" value={q.receiver_phone} /><Info label="Notes" value={q.notes} />
          <Info label="Emails" value={emailStatus(q.email_status)} />
        </div>
        <table className="w-full text-sm border border-slate-200">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600"><tr><th className="px-3 py-2 text-left">Item</th><th className="px-3 py-2 text-left">Size</th><th className="px-3 py-2 text-left">Qty</th><th className="px-3 py-2 text-right">Total</th></tr></thead>
          <tbody>
            {q.lines.map((l, i) => (
              <tr key={`${l.type}-${i}`} className="border-t border-slate-100">
                <td className="px-3 py-2">{l.label}{l.description && <div className="text-xs text-slate-500">{l.description}</div>}</td>
                <td className="px-3 py-2 text-xs">{l.length ? `${l.length}×${l.width}×${l.height} in · ${l.cubic_ft} cu ft` : "Fixed"}</td>
                <td className="px-3 py-2">{l.qty}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtMoney(l.total, q.currency)}</td>
              </tr>
            ))}
            <tr className="border-t border-slate-200 font-semibold"><td className="px-3 py-2" colSpan={3}>Pickup {fmtMoney(q.pickup_fee, q.currency)} · Lift-gate {fmtMoney(q.liftgate_fee, q.currency)} · Total</td><td className="px-3 py-2 text-right font-mono">{fmtMoney(q.total, q.currency)}</td></tr>
          </tbody>
        </table>
        <div><div className="text-xs font-bold uppercase tracking-wider text-[#1E2A78] mb-2">Cargo photos</div><QuotePhotos q={q} /></div>
        <button onClick={onReceipt} className="px-4 py-2 bg-[#1E2A78] text-white font-semibold rounded-sm hover:bg-[#17205E] inline-flex items-center gap-2 text-sm" data-testid="quote-create-receipt-btn">
          <FileSignature className="h-4 w-4" /> Create Warehouse Receipt
        </button>
      </div>
    </Modal>
  );
}

function emailStatus(s) {
  if (!s) return "Pending";
  if (s === "not_configured") return "Not sent (email not set up)";
  return Object.entries(s).map(([k, v]) => `${k === "team" ? "Team" : "Customer"}: ${v}`).join(" · ");
}

function receiptPrefill(q) {
  const [first, ...rest] = (q.full_name || "").trim().split(/\s+/);
  const [rFirst, ...rRest] = (q.receiver_name || "").trim().split(/\s+/);
  const cuft = (l) => (l.cubic_ft ? Math.round(l.cubic_ft * l.qty * 100) / 100 : undefined);
  return {
    date: todayISO(), quote_number: q.quote_number, origin: q.origin, destination: q.destination, currency: q.currency,
    door_to_door: q.method === "pickup" ? true : null,
    shipper: { first_name: first, last_name: rest.join(" "), address: q.address, city: q.city, state: q.province_state, zip_code: q.postal_code, phone: q.phone, email: q.email, country: q.currency === "USD" ? "USA" : "Canada" },
    consignee: { first_name: rFirst, last_name: rRest.join(" "), phone: q.receiver_phone, country: q.destination?.includes("Jamaica") ? "Jamaica" : q.destination, city: q.destination?.split(",")[0] },
    shipper_print_name: q.full_name,
    freight_charge: q.subtotal, pickup_charge: q.pickup_fee + q.liftgate_fee,
    items: q.lines.map((l) => ({ qty: l.qty, description: [l.label, l.description].filter(Boolean).join(" – "), length: l.length, width: l.width, height: l.height, cubic: cuft(l) })),
  };
}

export function QuotesTab() {
  const [quotes, setQuotes] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const load = useCallback(async () => {
    try { setQuotes((await api.get("/quotes")).data); } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openReceipt = async (q) => {
    const nextNumber = await api.get("/receipts/next-number").then((r) => r.data.preview).catch(() => "");
    setPrefill({ ...receiptPrefill(q), quoteId: q.id, nextNumber });
    setViewing(null);
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/quotes/${id}`, { status }); toast.success("Status updated"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="quotes-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
          <tr>{["Ref #", "Type", "Customer", "Route", "Total", "Photos", "Status", "Received"].map((h) => <th key={h} className="px-4 py-3 text-left">{h}</th>)}<th className="px-4 py-3 text-right">Actions</th></tr>
        </thead>
        <tbody>
          {quotes.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No quotes or bookings yet.</td></tr>}
          {quotes.map((q) => (
            <tr key={q.id} className="border-t border-slate-100" data-testid={`quote-row-${q.quote_number}`}>
              <td className="px-4 py-3 font-mono text-xs">{q.quote_number}</td>
              <td className="px-4 py-3"><TypeBadge q={q} /></td>
              <td className="px-4 py-3">{q.full_name}<div className="text-xs text-slate-500">{q.phone}</div></td>
              <td className="px-4 py-3 text-xs">{q.origin} → {q.destination}</td>
              <td className="px-4 py-3 font-mono text-xs">{fmtMoney(q.total, q.currency)}</td>
              <td className="px-4 py-3 text-xs"><span className="inline-flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" />{q.images.length}</span></td>
              <td className="px-4 py-3"><StatusBadge status={q.status} /></td>
              <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(q.created_at)}</td>
              <td className="px-4 py-3 text-right whitespace-nowrap">
                <button onClick={() => setViewing(q)} className="text-[#1E2A78] hover:underline mr-3 inline-flex items-center gap-1 text-xs" data-testid={`quote-view-${q.quote_number}`}><Eye className="h-3.5 w-3.5" /> View</button>
                <StatusSelect value={q.status} onChange={(s) => setStatus(q.id, s)} testId={`quote-status-${q.quote_number}`} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {viewing && <QuoteDetail q={viewing} onClose={() => setViewing(null)} onReceipt={() => openReceipt(viewing)} />}
      {prefill && <ReceiptFormModal prefill={prefill} nextNumber={prefill.nextNumber} onClose={() => setPrefill(null)} onSuccess={async () => { await setStatus(prefill.quoteId, "in_progress"); setPrefill(null); }} />}
    </div>
  );
}
